import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/firestore';
import { map } from 'rxjs/operators';
import { AppHttpClientService } from '../shared/app-http-client.service';
import {
  BidLot,
  LotInfo,
  LotData,
  NextLotInfo,
  resolveBidOriginType,
} from './panel.model';

interface PanelStateResponse {
  active: boolean;
  actualLot: string;
  isPaused: boolean;
  isCutting: boolean;
  isEditing: boolean;
  videoMode: string;
  lot: any;
}

const toLotInfo = (info: any): LotInfo =>
  new LotInfo(info.active, info.actualLot, info.paused, info.videoMode, info.cutting, info.editing);

const toLotData = (info: any): LotData => {
  if (!info) {
    throw new Error('Info not found');
  }
  return new LotData(
    info.code,
    info.increment,
    info.sysPrice,
    info.basePrice,
    info.type,
    info.weight,
    info.quantity,
    info.category,
    info.video || '',
    info.origin || '',
    info.providerName || '',
    info.observations || '',
    info.arrivedTime || '',
  );
};

@Injectable({
  providedIn: 'root',
})
export class PanelService {
  constructor(private db: AngularFirestore, private appHttpClient: AppHttpClientService) {}

  public getLotConfig(auctionId: string) {
    return this.db
      .collection(auctionId)
      .doc('panel')
      .snapshotChanges()
      .pipe(map((doc) => toLotInfo(doc.payload.data())));
  }

  public getLotById(auctionId: string, lotId: string) {
    return this.db
      .collection(auctionId)
      .doc('sections')
      .collection('lots')
      .doc(lotId)
      .snapshotChanges()
      .pipe(map((doc) => toLotData(doc.payload.data())));
  }

  /**
   * Server-side snapshot of the panel doc + current lot (see get_panel_state.ts in the
   * functions backend). Used to force-sync UI state right after a write we know just
   * succeeded, instead of waiting on the browser's Firestore realtime listener, which has
   * been observed to stall silently for tens of seconds after a write.
   */
  public getPanelState(auctionId: string): Promise<{ config: LotInfo; lot: LotData | null }> {
    return this.appHttpClient
      .get<PanelStateResponse>(`auction/panel-state?auctionId=${auctionId}`)
      .pipe(
        map((state) => ({
          config: new LotInfo(
            state.active,
            state.actualLot,
            state.isPaused,
            state.videoMode,
            state.isCutting,
            state.isEditing,
          ),
          lot: state.lot ? toLotData(state.lot) : null,
        })),
      )
      .toPromise();
  }

  public getLotBids(auctionId: string, lotId: string, limit: number = 5) {
    return this.db
      .collection(auctionId)
      .doc('sections')
      .collection('bids')
      .doc(lotId)
      .collection('private')
      .doc('bids')
      .snapshotChanges()
      .pipe(
        map((bidsnap) => {
          const bidValues = bidsnap.payload.data();
          const bids = [];
          if (!bidValues) {
            return bids;
          }

          const bidKeys = Object.keys(bidValues).filter((k) => k !== 'highest');
          for (const bidKey of bidKeys.sort((a, b) => +b - +a)) {
            if (!bidValues[bidKey].usuario) {
              continue;
            }

            const bidEntry = bidValues[bidKey];
            const isAssistedPending =
              bidEntry.asistida === true && bidEntry.asistidaPendiente !== false;
            bids.push(
              new BidLot(
                bidEntry.fecha,
                bidEntry.usuario,
                bidEntry.valor,
                bidEntry.cliente,
                resolveBidOriginType(bidEntry.source, bidEntry.asistida),
                isAssistedPending,
              ),
            );

            if (limit > 0 && bids.length === limit) {
              break;
            }
          }

          return bids;
        }),
      );
  }

  public activateLot(auctionId: string) {
    return this.appHttpClient.put('auction/activate', { auctionId });
  }

  public finishLot(auctionId: string) {
    return this.appHttpClient.delete('auction/inactivate', '', 'auctionId=' + auctionId);
  }

  public pauseLot(auctionId: string) {
    return this.appHttpClient.put('auction/pause', { auctionId });
  }

  public resumeLot(auctionId: string) {
    return this.appHttpClient.put('auction/resume', { auctionId });
  }

  public changeActualLot(auctionId: string, nextLot: NextLotInfo) {
    return this.appHttpClient.post('auction/publish', { auctionId, ...nextLot });
  }

  public deleteLastBid(auctionId: string, lotId: string) {
    return this.appHttpClient.delete('bids/last', lotId, 'auctionId=' + auctionId);
  }

  public setVideoMode(auctionId: string, videoMode: string) {
    return this.appHttpClient.put('auction/video-mode', { auctionId, videoMode });
  }

  public assignAssistedBid(
    auctionId: string,
    lotId: string,
    userId: string,
  ): Promise<void> {
    return this.appHttpClient.put(`bids/${lotId}/assign`, { auctionId, userId });
  }

  public sendNotification(users: string[], title: string, description: string): Promise<void> {
    return this.appHttpClient.post('notifications', { users, title, description });
  }
}
